import { Outlet, Navigate, useLocation, ScrollRestoration } from 'react-router';
import Header from '@/component/inc/header.jsx';
import Footer from '@/component/inc/footer.jsx';
import { NavigationProgress } from './NavigationProgress';
import useIsAuthenticated from '@/hooks/useAuth.js';
import { useGetProfileQuery, useGetVerificationsQuery } from '@/redux/api/userApi';
import { PATHS } from '@/navigation/paths';
import { passesIdGateOnProfile } from '@gearghost/api-schemas/user';

/**
 * Routes that unverified users are allowed to visit without being
 * redirected to the ID-verification page. These correspond to the
 * informational links shown in the footer, plus the /lastlooks QR
 * landing page — a marketing entry point that must never eject a
 * visitor, whatever their auth state.
 */
const UNVERIFIED_ALLOWED_PATHS = new Set([
  PATHS.aboutUs,
  PATHS.howItWorks,
  PATHS.contactUs,
  // An unverified user heading here to DELETE their account must never
  // be bounced to /verifyid on the way (Play Data safety URL).
  PATHS.deleteAccount,
  PATHS.damageAndTheft,
  // Every guide the footer's Guides page lists, and the page itself.
  PATHS.guides,
  PATHS.savingsTiers,
  PATHS.salesTax,
  PATHS.listingsGuide,
  // Off the Guides page, but a guide all the same: a store's developer reads it.
  PATHS.vendorApiGuide,
  // The blog's list: pages to read, as the guides are.
  PATHS.blog,
  PATHS.termsOfService,
  PATHS.privacyPolicy,
  PATHS.rulesAndGuidelines,
  PATHS.lastlooks,
]);

/**
 * RootLayout - Public layout with Header and Footer.
 * Used for public pages (home, about, listings, etc).
 * Child routes render via <Outlet />.
 *
 * A logged-in user the app's ID gate does not let in is redirected to
 * /verifyid from most pages under this layout. The gate is the backend's
 * (GET user/verification's idGate, Tyler 2026-10-22): a verified ID passes
 * on the profile alone, and so does a check Stripe is still reviewing, by
 * the backend's word; checkout waits for the verdict (verifiedIdLoader).
 * Footer/informational pages remain accessible so the user can
 * still read Terms of Service, Privacy Policy, etc.
 * Unauthenticated visitors can browse freely.
 */
export function RootLayout() {
  const isAuthenticated = useIsAuthenticated();
  const { pathname } = useLocation();
  const { data: user } = useGetProfileQuery(undefined, {
    skip: !isAuthenticated,
  });

  const isOnVerifyPage = pathname === PATHS.verifyId;
  // A post's page sits under the blog's list, one address per post, so it
  // is matched by its folder rather than listed.
  const isBlogPost = pathname.startsWith(`${PATHS.blog}/`);
  const isAllowedForUnverified = UNVERIFIED_ALLOWED_PATHS.has(pathname) || isBlogPost;
  const gateApplies =
    isAuthenticated &&
    user != null &&
    !passesIdGateOnProfile(user) &&
    !isOnVerifyPage &&
    !isAllowedForUnverified;
  // Read only for someone a verified ID does not already let in. It stays
  // until the verification status is marked stale: a recorded check pushes
  // PROFILE_CHANGED, and useProfileSocketSync marks it so.
  const { data: verifications } = useGetVerificationsQuery(undefined, {
    skip: !gateApplies,
  });

  if (gateApplies && verifications?.idGate === 'verify') {
    return <Navigate to={PATHS.verifyId} replace state={{ showVerifyModal: true }} />;
  }

  return (
    <>
      {/*
        Default per-history-entry keys: every link click (PUSH) gets a
        fresh key with no saved position → lands at the top; back/forward
        (POP) reuses the entry's key → restores where the user was. The
        previous custom getKey keyed non-home routes by PATHNAME, which
        made any repeat visit to a page restore its last scroll position —
        footer links (clicked from the bottom of a page) appeared to do
        nothing on second visits.
      */}
      <ScrollRestoration />
      <NavigationProgress />
      <Header />
      <div className="page-wrapper">
        <Outlet />
      </div>
      <Footer />
    </>
  );
}

export default RootLayout;
